import { Capacitor } from "@capacitor/core";
import {
  SESSION_USER_CLAIMS,
  spErrorTags,
  spLogTags,
  USER_ADDITIONAL_INFO_SEVERITY,
  getUserPref,
} from "@eventr/common";
import { StateCreator } from "zustand";
import { routes } from "../app/routing";
import { resetAllStores } from "../feat/appState/resetState";
import { clearPendingAuthLink } from "../feat/deeplink/auth-deeplink";
import { resetToTabRoot, resumeNativeTabs } from "../feat/nativeNavigation/NativeRootController";
import { PushNotifications } from "../feat/push/plugin";
import { clearNotifications } from "../feat/push/pushProvider";
import { createChatStore } from "../feat/rocketchat/chatStore";
import { drainPushDiagnostics } from "../feat/push/diagnostics";
import { getNotificationPermissionState } from "../feat/rocketchat/notificationPermissionState";
import i18n from "../i18n/i18next";
import { Utils } from "../utils";
import { BoundStore } from "./bound.store";
import { useLiveLocationStore } from "./liveLocationState/liveLocation.store";
import { defaultAddSnack } from "./overlayState/overlayContext";
import { clearChatMediaCache } from "../feat/rocketchat/chatMediaCache";

interface AppEventState {
  refreshKey: number;
  showRequiredAddInfoDialog: boolean; //state
}
interface AppEventActions {
  onLogin: () => Promise<void>;
  logout: () => Promise<void>;
  removePushToken: () => void;
  onReturn: () => Promise<void>;
  onOpen: () => void;
  showRequiredUserAddInfoDialog: (value: boolean) => void;
}

export interface AppEventSlice extends AppEventState, AppEventActions {}

// Activations that land while a refresh is running join it rather than starting another.
let onReturnInFlight: Promise<void> | undefined;

export const createAppEventSlice: StateCreator<BoundStore, [["zustand/devtools", never]], [], AppEventSlice> = (
  set,
  get,
) => ({
  refreshKey: 0,
  showRequiredAddInfoDialog: false,
  onLogin: async () => {
    // A committed session wins over any link still resolving, returning the root to the tabs.
    clearPendingAuthLink();
    await get().getProfile();
    // resolve org based on userPrefs
    const defaultOrgPref = getUserPref("account", "defaultOrg");
    let orgToUse = null;
    if (defaultOrgPref) {
      orgToUse = get().profile?.organisations.find((org) => org.name === defaultOrgPref);
    } else {
      orgToUse = get().profile?.organisations[0];
    }
    if (orgToUse) {
      console.log("orgToUse", orgToUse);

      await get().setSelectedOrg(orgToUse);
      // Claims can still be settling right after login; retry until they land, same as onReturn().
      get()
        .syncSubscription({ untilClaimsChange: true })
        .catch((error) => {
          spErrorTags("Error refreshing subscription on login", ["SUBSCRIPTION"], error);
        });
    }
    createChatStore();
    set({ loginCompleted: true }, false, "onLogin");

    await get().setProfileShareSections();
    const isProfileRequiredInfoIncomplete = get().profileRequiredAdditionalInfo?.length === 0 ? false : true;

    const severityLevel = get().getOrgUsersSeverityLevel();
    if (isProfileRequiredInfoIncomplete && severityLevel >= USER_ADDITIONAL_INFO_SEVERITY.medium) {
      get().showRequiredUserAddInfoDialog(isProfileRequiredInfoIncomplete);
    }
  },

  logout: async () => {
    spLogTags("Full logout called", ["DEBUG"]);
    // deleting userOrgPreferences should be after this function
    get().clearCalendars();
    // The queue outlives resetAllStores, so it has to be emptied explicitly.
    get().clearFavourites();
    get().clearNotes();
    localStorage.removeItem(SESSION_USER_CLAIMS);
    localStorage.removeItem("userOrgPreferences");
    localStorage.removeItem("userPreferences");
    indexedDB.deleteDatabase("chatStore");
    void clearChatMediaCache();
    useLiveLocationStore.setState({ sharingStaleTimestamp: undefined });
    Utils.logoutClient();
    get().removePushToken();
    resetAllStores();
  },
  removePushToken: () => {
    if (Capacitor.isNativePlatform()) {
      PushNotifications.unregister();
    }
  },

  onReturn: () => {
    onReturnInFlight ??= (async () => {
      if (get().IDtoken) {
        // Backstop for the native tab bar. Its set is otherwise only reconsidered when a flag
        // moves, so a reflow that failed while the app was in the foreground would survive
        // until the process was killed. A set that already matches is a no-op, and a failure
        // that took the root down with it is rebuilt rather than reflowed.
        void resumeNativeTabs();
        await get().getProfile();
        // resolve org based on userPrefs
        const defaultOrgPref = getUserPref("account", "defaultOrg");
        if (defaultOrgPref && defaultOrgPref !== get().selectedOrg?.name) {
          const orgToUse = get().profile?.organisations.find((org) => org.name === defaultOrgPref);
          if (!orgToUse) {
            return;
          }
          await get().setSelectedOrg(orgToUse);
          return;
        }
        set({ refreshKey: Math.random() });
        // make all org request
        drainPushDiagnostics();
        getNotificationPermissionState();
        clearNotifications();
        const promises = [];
        promises.push(
          // forceItineraryMapUpdate below runs alongside and skips the fetch with no WordPress user,
          // so if this one ends without one the map is settled here, as a failed fetch would be.
          get()
            .fetchUsers()
            .then((claimsStored) => {
              if (!get().wpUser) set({ itineraryMapSettled: true }, false, "itineraryMapSettled");
              return claimsStored;
            }),
          get().fetchOrgConfig(),
          get().fetchFields(),
          get().fetchSections(),
          get().setProfileShareSections(),
          get().forceItineraryMapUpdate(),
          get().loadRequests(),
          get().openWebsocket(),
          useLiveLocationStore.getState().init("app-resume"),
          // fetchOrgConfig above doesn't refresh tier; only syncSubscription's claims check does.
          get()
            .syncSubscription()
            .then(async () => {
              if (get().checkFeatureEnabled("Chat") || !window.location.pathname.startsWith(routes.chat.list.path)) {
                return;
              }
              if (!(await resetToTabRoot("account"))) {
                window.pushPath(routes.account.subscription.path);
              }
              defaultAddSnack(i18n.t("snack.chatEntitlementLost"), "warning");
            })
            .catch((error) => {
              spErrorTags("Error refreshing subscription on resume", ["SUBSCRIPTION"], error);
            }),
        );
        await Promise.all(promises);
      }
    })().finally(() => {
      onReturnInFlight = undefined;
    });
    return onReturnInFlight;
  },
  onOpen: async () => {
    createChatStore();
    get().initVersionConfig();
    get().onFirstOpened();
    get().setFirstColdStartAt();
  },
  showRequiredUserAddInfoDialog: (value: boolean) => {
    set({ showRequiredAddInfoDialog: value });
  },
});
